import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Heart } from "lucide-react";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | donation | timeout | error
  const [lookupKey, setLookupKey] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setLookupKey(data.lookup_key);
          const isMembership = data.lookup_key?.startsWith("membership");
          if (refresh) await refresh();
          setState(isMembership ? "paid" : "donation");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        console.error("Payment status poll failed:", e);
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-28 text-center">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 strokeWidth={1.5} className="w-12 h-12 text-oxblood mx-auto animate-spin" />
          <p className="font-serif text-3xl text-ink mt-8">Confirming your payment…</p>
          <p className="font-body text-text-muted mt-3">This only takes a moment.</p>
        </div>
      )}

      {state === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 strokeWidth={1.5} className="w-14 h-14 text-oxblood mx-auto" />
          <p className="font-serif text-4xl text-ink mt-8">Welcome to the circle</p>
          <p className="font-body text-text-muted mt-4 leading-relaxed">
            Your {lookupKey === "membership_lifetime" ? "lifetime" : lookupKey === "membership_annual" ? "annual" : "monthly"} membership is active.
            You can now join every discussion thread.
          </p>
          <Link
            to="/archive"
            data-testid="success-to-archive"
            className="inline-block mt-8 bg-oxblood text-parchment px-8 py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors"
          >
            Enter the Archive
          </Link>
        </div>
      )}

      {state === "donation" && (
        <div data-testid="payment-donation">
          <Heart strokeWidth={1.5} className="w-14 h-14 text-oxblood mx-auto" />
          <p className="font-serif text-4xl text-ink mt-8">Thank you</p>
          <p className="font-body text-text-muted mt-4 leading-relaxed">
            Your gift helps preserve the 1872 volume for readers to come.
          </p>
          <Link
            to="/archive"
            className="inline-block mt-8 bg-oxblood text-parchment px-8 py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors"
          >
            Back to the Archive
          </Link>
        </div>
      )}

      {(state === "timeout" || state === "error") && (
        <div data-testid="payment-error">
          <XCircle strokeWidth={1.5} className="w-14 h-14 text-oxblood mx-auto" />
          <p className="font-serif text-4xl text-ink mt-8">
            {state === "timeout" ? "Still processing" : "Something went wrong"}
          </p>
          <p className="font-body text-text-muted mt-4 leading-relaxed">
            {state === "timeout"
              ? "Your payment is taking longer than expected. If you completed checkout, your membership will appear shortly."
              : "We couldn't confirm this payment. No charge has been made if checkout was cancelled."}
          </p>
          <Link
            to="/membership"
            className="inline-block mt-8 bg-oxblood text-parchment px-8 py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors"
          >
            Back to Membership
          </Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => navigate("/membership"), 3000);
    return () => clearTimeout(t);
  }, [navigate]);
  return (
    <div className="max-w-xl mx-auto px-6 py-28 text-center" data-testid="payment-cancel">
      <XCircle strokeWidth={1.5} className="w-14 h-14 text-text-muted mx-auto" />
      <p className="font-serif text-4xl text-ink mt-8">Checkout cancelled</p>
      <p className="font-body text-text-muted mt-4">No charge was made. Returning you to the membership page…</p>
    </div>
  );
}
